// frontend/src/components/BatchTranslation.js - Enhanced version
import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import batchService from '../services/batch/batchService';
import translationService from '../services/translation/translationService';
import projectService from '../services/project/projectService';
import glossaryOptimizerService from '../services/glossary/glossaryOptimizerService';
import AlertMessage from './AlertMessage';
import '../styles/global.css';

/**
 * Enhanced BatchTranslation component with clearer state management
 * and better separation of concerns
 */
const BatchTranslation = ({ 
  documentId, 
  projectId,
  onBatchCreated, 
  onTranslationComplete,
  onError,
  onTranslationStart,
  disabled = false
}) => {
  const { creditBalance, updateCreditBalance } = useContext(AuthContext);
  const [projectDetails, setProjectDetails] = useState(null); // State to store project details
  
  // State
  const [translationState, setTranslationState] = useState({
    status: 'idle', // idle, creating, translating, completed, error
    batchId: null,
    batchNumber: null,
    message: '',
    error: '',
    creditsUsed: 0
  });
  
  // Reset translation state when documentId changes
  useEffect(() => {
    setTranslationState({
      status: 'idle',
      batchId: null, 
      batchNumber: null,
      message: '',
      error: '',
      creditsUsed: 0
    });
  }, [documentId]);

  // Fetch project details (like languages) when projectId changes or is available
  useEffect(() => {
    if (projectId) {
      const fetchProjectDetails = async () => {
        try {
          const details = await projectService.getProjectById(projectId);
          if (details && !details.error) {
            setProjectDetails(details);
          } else {
            console.error("Failed to fetch project details for BatchTranslation:", details?.message || "Unknown error");
            setProjectDetails(null); // Reset if error
          }
        } catch (error) {
          console.error("Error fetching project details for BatchTranslation:", error);
          setProjectDetails(null); // Reset on exception
        }
      };
      fetchProjectDetails();
    } else {
      setProjectDetails(null); // Reset if no projectId
    }
  }, [projectId]);
  
  // Clear error after delay
  useEffect(() => {
    if (translationState.error) {
      const timer = setTimeout(() => {
        setTranslationState(prev => ({...prev, error: ''}));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [translationState.error]);
  
  // Clear success message after delay
  useEffect(() => {
    if (translationState.message && translationState.status === 'completed') {
      const timer = setTimeout(() => {
        setTranslationState(prev => ({...prev, message: ''}));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [translationState.message, translationState.status]);
  
  /**
   * Start the translation process
   * This is a multi-step process:
   * 1. Create a batch
   * 2. Get glossary
   * 3. Translate the batch
   * 4. Update credit balance
   * 5. Notify parent components
   */
  const translateBatch = async () => {
    if (disabled || translationState.status === 'creating' || translationState.status === 'translating') {
      return;
    }
    
    // Update state to creating
    setTranslationState({
      status: 'creating',
      batchId: null,
      batchNumber: null,
      message: '',
      error: '',
      creditsUsed: 0
    });
    
    try {
      // 1. Create a batch
      const newBatch = await batchService.createBatch(documentId);
      
      // Handle case where all paragraphs are already translated
      if (newBatch.error && newBatch.type === 'NO_MORE_PARAGRAPHS') {
        setTranslationState({
          status: 'completed',
          batchId: null,
          batchNumber: null,
          message: 'All paragraphs have been translated!',
          error: '',
          creditsUsed: 0
        });
        
        // Notify parent that all batches are created
        if (onBatchCreated) {
          onBatchCreated(null, true);
        }
        
        return;
      }
      
      // Handle other batch creation errors
      if (newBatch.error) {
        throw new Error(newBatch.message || 'Failed to create batch');
      }
      
      // Update state with new batch
      setTranslationState(prev => ({
        ...prev,
        status: 'translating',
        batchId: newBatch.id,
        batchNumber: newBatch.batch_number
      }));
      
      // Notify parent that batch was created
      if (onBatchCreated) {
        onBatchCreated(newBatch);
      }
      
      // Notify parent that translation is starting
      if (onTranslationStart) {
        onTranslationStart(newBatch.id);
      }
      
      // 2. Get glossary for this project
      const fullGlossary = await projectService.getGlossary(projectId);
      let optimizedGlossaryForTranslation = [];
      // Use the projectDetails from the component's state
      const currentProjectDetails = projectDetails; 

      if (fullGlossary && fullGlossary.length > 0 && newBatch.original_text && currentProjectDetails?.source_language) {
        try {
          // console.log(`Optimizing glossary for new batch translation. Original size: ${fullGlossary.length}, Source Lang: ${currentProjectDetails.source_language}`);
          optimizedGlossaryForTranslation = await glossaryOptimizerService.optimizeGlossary(
            newBatch.original_text,
            fullGlossary,
            currentProjectDetails.source_language
          );
          // console.log(`Glossary optimized for translation. New size: ${optimizedGlossaryForTranslation.length}`);
        } catch (optError) {
          console.error("Error optimizing glossary for batch translation:", optError);
          optimizedGlossaryForTranslation = fullGlossary; // Fallback to full glossary
        }
      } else if (fullGlossary) {
        optimizedGlossaryForTranslation = fullGlossary; // Use full if no text or lang for optimization
      }
      
      // 3. Translate the batch using the optimized glossary
      const result = await translationService.translateBatch(newBatch.id, optimizedGlossaryForTranslation);
      
      // Handle translation errors
      if (result.error) {
        // Handle specific error types
        if (result.type === 'INSUFFICIENT_CREDITS') {
          setTranslationState(prev => ({
            ...prev,
            status: 'error',
            error: result.message || 'Insufficient credits'
          }));
          
          if (onError) {
            onError('credits', result);
          }
        } else if (result.type === 'SERVICE_OVERLOADED') {
          setTranslationState(prev => ({
            ...prev,
            status: 'error',
            error: 'Translation service is currently overloaded. Please try again later.'
          }));
          
          if (onError) {
            onError('overloaded', result);
          }
        } else {
          setTranslationState(prev => ({
            ...prev,
            status: 'error',
            error: result.message || 'Translation failed'
          }));
          
          if (onError) {
            onError('general', result);
          }
        }
        return;
      }
      
      // 4. Update credit balance
      await updateCreditBalance();
      
      // 5. Update state with success
      const creditsUsed = result.credits_used || 0;
      setTranslationState({
        status: 'completed',
        batchId: newBatch.id,
        batchNumber: newBatch.batch_number,
        message: `Batch ${newBatch.batch_number} translated successfully (${creditsUsed.toLocaleString()} credits used)`,
        error: '',
        creditsUsed
      });
      
      // 6. Notify parent of completion
      if (onTranslationComplete) {
        onTranslationComplete(newBatch.id, result);
      }
      
    } catch (error) {
      console.error("Translation error:", error);
      
      setTranslationState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Translation failed'
      }));
      
      if (onError) {
        onError('general', error);
      }
    }
  };

  return (
    <div className="translate-button-container">
      {translationState.error && (
        <AlertMessage 
          message={translationState.error} 
          type="error" 
          onDismiss={() => setTranslationState(prev => ({...prev, error: ''}))} 
        />
      )}
      
      {translationState.message && (
        <AlertMessage 
          message={translationState.message} 
          type="success" 
          onDismiss={() => setTranslationState(prev => ({...prev, message: ''}))} 
        />
      )}
      
      <button 
        onClick={translationState.status === 'creating' || translationState.status === 'translating' 
          ? null 
          : translateBatch}
        disabled={disabled || 
          translationState.status === 'creating' || 
          translationState.status === 'translating'}
        className={`button button-primary ${creditBalance < 5000 && creditBalance > 0 ? 'credit-warning' : ''}`}
      >
        {translationState.status === 'creating' ? (
          'Creating Batch...'
        ) : translationState.status === 'translating' ? (
          'Translating...'
        ) : creditBalance <= 0 ? (
          'Add Credits to Translate'
        ) : (
          'Translate Next Batch'
        )}
      </button>
      
      {creditBalance <= 5000 && creditBalance > 0 && translationState.status === 'idle' && !disabled && (
        <div className="text-xs text-amber-600 mt-2">
          Your credit balance is low ({creditBalance} credits remaining)
        </div>
      )}
    </div>
  );
};

export default BatchTranslation;